import { XMarkIcon } from '@heroicons/react/24/outline';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { DialogProps } from '@radix-ui/react-dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { createContext, useContext } from 'react';
import { cn } from 'shared/src/utils';
import { twMerge } from 'tailwind-merge';
import type { dialogAdornerVariants } from '../Dialog';
import { ScrollArea } from '../ScrollArea';

const DialogRoot = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-black/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  ),
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const dialogSizeVariants = cva(
  'fixed left-[50%] top-[50%] rounded-md z-50 overflow-hidden grid w-[95%] sm:w-full translate-x-[-50%] translate-y-[-50%] gap-4 border border-accent bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
  {
    variants: {
      size: {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        most: 'xl:w-[75%] xl:h-[90%] w-[85%] h-[90%] flex flex-col',
        full: 'w-full h-full flex flex-col rounded-none',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { showClose?: boolean; disableScrollArea?: boolean }
>(({ className, children, showClose = true, disableScrollArea = false, ...props }, ref) => {
  const { size }: VariantProps<typeof dialogSizeVariants> = useContext(DialogContext);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content ref={ref} aria-describedby={undefined} className={cn(size, twMerge(cn(dialogSizeVariants({ size, className }))))} {...props}>
        {size === 'full' || size === 'most' || disableScrollArea ? children : <ScrollArea className="max-h-dvh">{children}</ScrollArea>}
        {showClose ? (
          <DialogPrimitive.Close
            tabIndex={0}
            className="absolute right-4 top-3.5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <XMarkIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col -space-y-0.5 text-center sm:text-left', className)} {...props}>
    <div className="px-4 py-3">{children}</div>
    <div className="bg-gradient-to-r from-enhancetitle via-enhancetitle/80 to-enhance h-[1px] drop-shadow" />
  </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('px-4 py-3', className)} {...props} />;
DialogBody.displayName = 'DialogBody';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-4 py-3 sticky bottom-0 bg-background', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(
  ({ className, ...props }, ref) => <DialogPrimitive.Title ref={ref} className={cn('font-semibold leading-none tracking-tight', className)} {...props} />,
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(
  ({ className, ...props }, ref) => <DialogPrimitive.Description ref={ref} className={cn('text-sm pb-4 text-muted-foreground', className)} {...props} />,
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogContext = createContext<VariantProps<typeof dialogAdornerVariants>>({ variant: 'default' });

const Dialog = ({ variant, size, ...props }: VariantProps<typeof dialogAdornerVariants> & DialogProps) => {
  return (
    <DialogContext.Provider
      value={{
        variant,
        size,
      }}
    >
      <DialogRoot {...props} />
    </DialogContext.Provider>
  );
};

export { Dialog, DialogPortal, DialogOverlay, DialogTrigger, DialogClose, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogBody, DialogDescription };
